import { logDataPartnerCarHireExit } from "src/data-partners/logDataPartnerExit";
import { useIntl } from "react-intl";
import { useExitLink, type ExitLinkProps } from "src/exits/hooks/useExitLink";
import { generateExitPoint } from "src/analytics/exits/generateExitPoint/generateExitPoint";
import { getCarHireDeeplinkRedirectUrl } from "../../utils/url";
import { preferredNameFromPlace } from "../../utils/adapters/place";
import { localeToLanguageCode } from "../../utils/conversions/languageCode";
import { getCarHirePickupPlace } from "../../utils/getCarHirePickupPlace";
import type { StructEventParams } from "../../utils/types/snowplow";
import useSearch from "../../utils/hooks/useSearch";

export function useCarHireExitLink({
  event,
  carSize,
  exitPoint,
}: {
  event: StructEventParams;
  exitPoint: ExitLinkProps["exitPoint"];
  carSize?: "small" | "medium" | "large";
}) {
  const { searchResponse } = useSearch();
  const intl = useIntl();

  const { url, originCanonical, destinationCanonical } = getCarHireExitUrl();

  return useExitLink({
    href: url ?? "",
    kind: "RentalCars",
    exitPoint: {
      transitMode: "car",
      ...exitPoint,
    },
    onExit({ url, sendExitInteractionEvent }) {
      sendExitInteractionEvent(event);

      logDataPartnerCarHireExit({
        origin: originCanonical,
        destination: destinationCanonical,
        languageCode: localeToLanguageCode(intl.locale),
        isAdaraEnabled:
          searchResponse?.analytics?.interest_data?.Adara?.enabled,
      });

      window.open(url, "_blank", "noopener noreferrer");
    },
  });

  function getCarHireExitUrl(): {
    url?: string;
    originCanonical?: string;
    destinationCanonical?: string;
  } {
    if (!searchResponse) {
      return {
        url: undefined,
        originCanonical: undefined,
        destinationCanonical: undefined,
      };
    }

    const requestId = searchResponse.request.requestId;

    const { carPickupPlace, originPlace, destinationPlace } =
      getCarHirePickupPlace(searchResponse);

    return {
      url: getCarHireDeeplinkRedirectUrl(
        requestId,
        generateExitPoint(
          exitPoint.view,
          exitPoint.trigger,
          exitPoint.transitMode,
        ),
        preferredNameFromPlace(carPickupPlace),
        carPickupPlace.lat,
        carPickupPlace.lng,
        carSize,
      ),
      originCanonical: originPlace.canonicalName,
      destinationCanonical: destinationPlace.canonicalName,
    };
  }
}
